import React, { useEffect, useState, useMemo } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Settings as SettingsIcon, QrCode, Copy, Download, ExternalLink, Printer, RefreshCw, AlertTriangle, Trash2, ShieldAlert } from "lucide-react";

const Impostazioni = () => {
    const [customerSettings, setCustomerSettings] = useState({ ordering_enabled: false, call_waiter_enabled: true });
    const [tables, setTables] = useState([]);
    const [filter, setFilter] = useState("");
    const [regenerating, setRegenerating] = useState(null);
    const [resetModal, setResetModal] = useState(false);
    const [resetAck, setResetAck] = useState(false);
    const [resetBusy, setResetBusy] = useState(false);
    const origin = window.location.origin;
    const customerUrl = `${origin}/c`;

    const tableUrl = (code) => `${origin}/c?t=${encodeURIComponent(code)}`;

    const load = async () => {
        try {
            const [s, t] = await Promise.all([
                api.get("/customer/settings"),
                api.get("/tables"),
            ]);
            setCustomerSettings(s.data);
            setTables((t.data || []).sort((a, b) => {
                const an = parseInt(a.number, 10); const bn = parseInt(b.number, 10);
                if (!isNaN(an) && !isNaN(bn)) return an - bn;
                return String(a.number).localeCompare(String(b.number));
            }));
        } catch (_e) { /* */ }
    };
    useEffect(() => { load(); }, []);

    const toggle = async (key) => {
        const newValue = !customerSettings[key];
        try {
            await api.post("/customer/settings", { ...customerSettings, [key]: newValue });
            setCustomerSettings({ ...customerSettings, [key]: newValue });
            toast.success("Impostazione aggiornata");
        } catch (e) { toast.error(formatError(e)); }
    };

    const copy = (txt, label = "Link") => {
        navigator.clipboard.writeText(txt);
        toast.success(`${label} copiato`);
    };

    const regenCode = async (tid) => {
        if (!window.confirm("Rigenerare il codice annulla i QR già stampati. Procedere?")) return;
        setRegenerating(tid);
        try {
            await api.post(`/tables/${tid}/regenerate-code`);
            await load();
            toast.success("Codice rigenerato");
        } catch (e) { toast.error(formatError(e)); }
        setRegenerating(null);
    };

    const resetHistory = async () => {
        setResetBusy(true);
        try {
            const { data } = await api.post("/restaurant/reset-history", { confirm: true });
            const counts = data.deleted_counts || {};
            const total = Object.values(counts).reduce((s, n) => s + n, 0);
            toast.success(`Storico cancellato: ${total} righe rimosse. Backup salvato (30gg).`, { duration: 6000 });
            setResetModal(false);
            setResetAck(false);
        } catch (e) {
            toast.error(formatError(e));
        }
        setResetBusy(false);
    };

    const downloadSingleQR = (tableNumber, code) => {
        const svg = document.querySelector(`[data-testid="table-qr-${tableNumber}"] svg`);
        if (!svg) return;
        const serializer = new XMLSerializer();
        const source = serializer.serializeToString(svg);
        const blob = new Blob([source], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `smart-table-tavolo-${tableNumber}-${code}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadGlobalQR = () => {
        const svg = document.querySelector('[data-testid="customer-qr"] svg');
        if (!svg) return;
        const serializer = new XMLSerializer();
        const source = serializer.serializeToString(svg);
        const blob = new Blob([source], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "smart-table-qr-generale.svg";
        a.click();
        URL.revokeObjectURL(url);
    };

    const printAll = () => {
        // Genera una pagina A4 stampabile con 6 QR (2 colonne × 3 righe), pronti per ritaglio.
        const win = window.open("", "_blank");
        if (!win) { toast.error("Popup bloccato dal browser"); return; }
        const cards = filtered.map((t) => {
            const url = tableUrl(t.short_code);
            return `
                <div class="card">
                    <div class="hdr">SMART TABLE · INQUADRA IL QR</div>
                    <div class="body">
                        <div class="left">
                            <div class="num-label">TAVOLO</div>
                            <div class="num">${t.number}</div>
                            <div class="code">${t.short_code}</div>
                        </div>
                        <div class="qr">
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=480x480&ecc=H&margin=2&data=${encodeURIComponent(url)}" alt="QR Tavolo ${t.number}" />
                        </div>
                    </div>
                    <div class="cta">Menu, ordini e chiamata cameriere · ritaglia lungo il bordo</div>
                </div>`;
        }).join("");
        win.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>QR Tavoli — Smart Table (6 per A4)</title>
        <style>
        @page { size: A4 portrait; margin: 6mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: #fff; color: #0A1220; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; }
        .grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: 92mm;
            gap: 0;
        }
        .card {
            border: 1px dashed #888;
            padding: 5mm;
            text-align: center;
            page-break-inside: avoid;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            background: #fff;
        }
        .hdr {
            font-size: 7pt;
            letter-spacing: 3pt;
            color: #2BA889;
            font-weight: 800;
            border-bottom: 1px solid #0A1220;
            padding-bottom: 2mm;
            margin-bottom: 3mm;
        }
        .body { display: flex; align-items: center; gap: 4mm; flex: 1; }
        .left { flex: 1; text-align: left; padding-left: 2mm; }
        .num-label { font-size: 8pt; letter-spacing: 3pt; color: #555; }
        .num { font-size: 56pt; font-weight: 900; line-height: 0.95; color: #0A1220; }
        .code {
            font-family: 'Courier New', 'Courier', monospace;
            font-size: 10pt;
            letter-spacing: 1.5pt;
            font-weight: 700;
            color: #0A1220;
            margin-top: 3mm;
            border-top: 1px solid #0A1220;
            padding-top: 2mm;
            display: inline-block;
        }
        .qr { width: 50mm; height: 50mm; flex-shrink: 0; }
        .qr img { width: 100%; height: 100%; }
        .cta { font-size: 7pt; color: #555; margin-top: 3mm; line-height: 1.3; }

        .toolbar { padding: 12px 16px; background: #f5f5f0; border-bottom: 1px solid #ddd; text-align: center; position: sticky; top: 0; z-index: 10; }
        .toolbar button { padding: 9px 18px; font-size: 14px; background: #2BA889; color: #fff; border: 0; border-radius: 4px; cursor: pointer; margin: 0 6px; font-weight: 700; }
        .toolbar button.secondary { background: #1f2937; }
        .toolbar .info { display: inline-block; margin-left: 18px; color: #666; font-size: 12px; }
        @media print {
            .toolbar { display: none; }
            .card { border: 1px dashed #999; }
        }
        </style>
        </head><body>
        <div class="toolbar">
            <button onclick="window.print()">Stampa / Salva come PDF</button>
            <button class="secondary" onclick="window.close()">Chiudi</button>
            <span class="info">${filtered.length} QR · 6 per pagina A4 · ritaglia lungo le linee tratteggiate</span>
        </div>
        <div class="grid">${cards}</div>
        </body></html>`);
        win.document.close();
    };

    const filtered = useMemo(() => {
        if (!filter) return tables;
        const q = filter.toLowerCase();
        return tables.filter((t) =>
            String(t.number).toLowerCase().includes(q) ||
            (t.short_code || "").toLowerCase().includes(q)
        );
    }, [tables, filter]);

    return (
        <div className="space-y-6 max-w-6xl" data-testid="impostazioni-page">
            <div>
                <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Sistema</div>
                <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                    <SettingsIcon className="w-9 h-9 text-brand" /> Impostazioni
                </h1>
            </div>

            {/* Link generale + toggles */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6">
                <div className="flex items-center gap-2 mb-1">
                    <QrCode className="w-5 h-5 text-brand" />
                    <div className="font-display text-xl font-bold">Portale Clienti — Link Generale</div>
                </div>
                <p className="text-elegant text-sm mb-6">
                    Link valido per ingresso senza tavolo predefinito (richiede check-in manuale).
                    Per ogni tavolo usa invece il QR specifico qui sotto: porta direttamente al tavolo
                    senza inserire alcun numero.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-5 rounded-lg flex items-center justify-center" data-testid="customer-qr">
                        <QRCodeSVG value={customerUrl} size={180} bgColor="#ffffff" fgColor="#0A1220" level="H" includeMargin={false} />
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-elegant">Link Pubblico</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input readOnly value={customerUrl} className="flex-1 h-10 px-3 bg-deep border border-[#1f2c44] rounded-md text-sm font-mono text-warm" data-testid="customer-url" />
                                <button onClick={() => copy(customerUrl)} title="Copia" className="w-10 h-10 rounded border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand flex items-center justify-center">
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={downloadGlobalQR} data-testid="download-qr-button"
                                className="flex-1 h-12 bg-brand hover:bg-brand-light text-deep font-bold rounded-md flex items-center justify-center gap-2">
                                <Download className="w-4 h-4" /> Scarica QR generale
                            </button>
                            <a href={customerUrl} target="_blank" rel="noopener noreferrer" data-testid="open-customer-link"
                                className="h-12 px-4 border border-brand text-brand hover:bg-brand-soft rounded-md flex items-center gap-2 transition-colors">
                                <ExternalLink className="w-4 h-4" /> Apri
                            </a>
                        </div>
                        <div className="border-t border-[#1f2c44] pt-4 space-y-3">
                            <Toggle
                                label="Ordinazione self-service"
                                description="Il cliente può aggiungere piatti e inviare l'ordine in cucina autonomamente."
                                checked={customerSettings.ordering_enabled}
                                onChange={() => toggle("ordering_enabled")}
                                testId="toggle-ordering"
                            />
                            <Toggle
                                label="Chiama cameriere"
                                description="Il cliente può chiamare il cameriere con una notifica."
                                checked={customerSettings.call_waiter_enabled}
                                onChange={() => toggle("call_waiter_enabled")}
                                testId="toggle-call"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* QR per tavolo */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6" data-testid="qr-per-table-section">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <QrCode className="w-5 h-5 text-brand" />
                            <div className="font-display text-xl font-bold">QR univoco per ogni tavolo</div>
                        </div>
                        <p className="text-elegant text-sm mt-2 max-w-2xl">
                            Stampa il QR di ogni tavolo: il cliente lo scansiona, accede con Google e
                            il check-in al tavolo è automatico. In alternativa può digitare il codice breve
                            (es. <span className="font-mono text-brand">TAV-12-AB3F</span>).
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filtra per numero o codice..."
                            data-testid="table-filter"
                            className="h-11 px-3 bg-deep border border-[#1f2c44] rounded-md text-sm text-warm focus:border-brand focus:outline-none"
                        />
                        <button onClick={printAll} disabled={!filtered.length} data-testid="print-all-qr"
                            className="h-11 px-4 bg-brand hover:bg-brand-light text-deep font-bold rounded-md flex items-center gap-2 disabled:opacity-50">
                            <Printer className="w-4 h-4" /> Stampa tutti (6/A4)
                        </button>
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="text-elegant text-sm py-8 text-center">Nessun tavolo presente. Aggiungi un tavolo dalla Mappa Sala.</div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filtered.map((t) => (
                            <div key={t.id} className="bg-deep border border-[#1f2c44] rounded-lg p-4 text-center" data-testid={`table-qr-card-${t.number}`}>
                                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">Tavolo</div>
                                <div className="font-display text-3xl font-bold text-warm leading-none mb-3">N° {t.number}</div>
                                <div className="bg-white p-3 rounded mb-3 inline-flex" data-testid={`table-qr-${t.number}`}>
                                    <QRCodeSVG value={tableUrl(t.short_code || "")} size={140} bgColor="#ffffff" fgColor="#0A1220" level="H" />
                                </div>
                                <div className="font-mono text-[11px] tracking-wider text-elegant mb-3 break-all" data-testid={`table-code-${t.number}`}>
                                    {t.short_code || "—"}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={() => copy(tableUrl(t.short_code || ""), `Link tavolo ${t.number}`)} data-testid={`copy-qr-link-${t.number}`}
                                        className="h-9 px-2 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center justify-center gap-1 text-xs">
                                        <Copy className="w-3 h-3" /> Link
                                    </button>
                                    <button onClick={() => downloadSingleQR(t.number, t.short_code)} data-testid={`download-qr-${t.number}`}
                                        className="h-9 px-2 bg-brand hover:bg-brand-light text-deep font-bold rounded-md flex items-center justify-center gap-1 text-xs">
                                        <Download className="w-3 h-3" /> SVG
                                    </button>
                                </div>
                                <button onClick={() => regenCode(t.id)} disabled={regenerating === t.id} data-testid={`regen-qr-${t.number}`}
                                    className="mt-2 h-8 w-full text-[10px] uppercase tracking-wider text-elegant hover:text-red-400 flex items-center justify-center gap-1">
                                    <RefreshCw className={`w-3 h-3 ${regenerating === t.id ? "animate-spin" : ""}`} /> Rigenera codice
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Zona Pericolosa — Reset storico */}
            <div className="bg-red-950/20 border-2 border-red-700/50 rounded-lg p-6" data-testid="danger-zone">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert className="w-5 h-5 text-red-300" />
                    <div className="font-display text-xl font-bold text-red-200">Zona Pericolosa</div>
                </div>
                <p className="text-red-300/80 text-sm mb-4">
                    Cancella TUTTO lo storico operativo del ristorante: ordini, pagamenti, scontrini/fatture,
                    chiusure fiscali, log attività, chiamate camerieri, job di stampa.
                    <br/>
                    <strong className="text-red-200">Restano intatti</strong>: menu, tavoli, utenti, stampanti,
                    abbonamento, dati di fatturazione.
                </p>
                <div className="text-xs text-amber-300/90 bg-amber-900/20 border border-amber-600/40 rounded p-2 mb-4 flex gap-2 items-start">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Prima della cancellazione viene creato un <strong>backup JSON</strong> conservato
                    per 30 giorni in <code className="font-mono text-[11px]">/app/backend/backups/</code>.
                    Per recuperare in emergenza, contatta il supporto.</span>
                </div>
                <button
                    onClick={() => { setResetAck(false); setResetModal(true); }}
                    data-testid="reset-history-open"
                    className="h-11 px-5 bg-red-700 hover:bg-red-600 text-white font-bold rounded-md uppercase tracking-wider text-xs flex items-center gap-2"
                >
                    <Trash2 className="w-4 h-4" /> Cancella storico ristorante
                </button>
            </div>

            {/* Modale conferma reset */}
            {resetModal && (
                <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" data-testid="reset-history-modal">
                    <div className="bg-surface border-2 border-red-500 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-red-900/40 border border-red-500 flex items-center justify-center">
                                <ShieldAlert className="w-6 h-6 text-red-300" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-red-300">Azione irreversibile</div>
                                <div className="font-display text-xl font-bold text-red-200">Reset storico ristorante</div>
                            </div>
                        </div>

                        <p className="text-sm text-elegant mb-3">
                            Stai per cancellare <strong className="text-red-200">tutto lo storico operativo</strong>.
                            I tavoli verranno riportati a "libero".
                        </p>
                        <ul className="text-xs text-elegant space-y-1 mb-4 bg-deep border border-[#1f2c44] rounded p-3">
                            <li>• Ordini e comande</li>
                            <li>• Pagamenti e scontrini/fatture</li>
                            <li>• Chiusure fiscali</li>
                            <li>• Log attività</li>
                            <li>• Chiamate camerieri (archivio)</li>
                            <li>• Job di stampa</li>
                        </ul>

                        <div className="bg-emerald-900/20 border border-emerald-600/40 rounded p-2 mb-4 text-xs text-emerald-200 flex gap-2 items-start">
                            <span className="text-emerald-400 font-bold">✓</span>
                            <span>Restano: <strong>menu, tavoli, utenti, stampanti, dati fatturazione, abbonamento</strong>.
                            Verrà creato un backup JSON di sicurezza (conservato 30 giorni).</span>
                        </div>

                        <label className="flex items-start gap-2 cursor-pointer mb-4 select-none">
                            <input
                                type="checkbox"
                                checked={resetAck}
                                onChange={(e) => setResetAck(e.target.checked)}
                                data-testid="reset-history-ack"
                                className="mt-0.5 w-5 h-5 accent-red-500"
                            />
                            <span className="text-sm text-warm">Ho capito, perdo tutti i dati operativi. Procedi.</span>
                        </label>

                        <div className="flex gap-2">
                            <button
                                onClick={() => { setResetModal(false); setResetAck(false); }}
                                data-testid="reset-history-cancel"
                                disabled={resetBusy}
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={resetHistory}
                                disabled={!resetAck || resetBusy}
                                data-testid="reset-history-confirm"
                                className="flex-1 h-12 bg-red-700 hover:bg-red-600 disabled:bg-red-900/30 disabled:cursor-not-allowed text-white font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2"
                            >
                                {resetBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                {resetBusy ? "Cancellazione..." : "Cancella storico"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* App info */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6">
                <div className="font-display text-xl font-bold mb-3">Smart Table by renova360</div>
                <div className="text-sm text-elegant space-y-2">
                    <div>Versione: 1.4.0 · QR per tavolo + Sync Bidirezionale Prenova</div>
                    <div>Stato Realtime: <span className="text-brand">● connesso</span></div>
                    <div>Database: persistente (MongoDB)</div>
                    <div>AI Engine: Claude Sonnet 4.5</div>
                </div>
            </div>
        </div>
    );
};

const Toggle = ({ label, description, checked, onChange, testId }) => (
    <button onClick={onChange} data-testid={testId} className="w-full text-left flex items-start gap-3 p-3 rounded-md hover:bg-deep transition-colors">
        <div className={`mt-0.5 w-11 h-6 rounded-full relative flex-shrink-0 transition-colors ${checked ? "bg-brand" : "bg-[#1f2c44]"}`}>
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
        </div>
        <div className="flex-1">
            <div className="text-sm font-bold text-warm">{label}</div>
            <div className="text-xs text-elegant">{description}</div>
        </div>
    </button>
);

export default Impostazioni;
